// ============================================================================
// Systèmes d'équations LINÉAIRES (lot 5 de la refonte, docs/moteur/REFONTE_MOTEUR.md).
//
// Une équation « l = r » est linéaire quand l − r se normalise en
//     a₁·x₁ + … + aₙ·xₙ + c   (aᵢ, c rationnels, dénominateur constant).
// On la représente par sa FORME { coef, c } (« … = 0 »).
//
// Fondement mathématique (exact, sur ℚ) : si un système est COMPATIBLE, une
// équation linéaire s'annule sur toutes ses solutions SI ET SEULEMENT SI sa
// forme est une combinaison linéaire des formes du système. Un système est
// INCOMPATIBLE si et seulement si la forme « 1 = 0 » en est une combinaison.
// Tout se décide donc par élimination de Gauss exacte, sans tirage au hasard.
//
// Pur, sans dépendance hors du noyau.
// ============================================================================

import * as Q from './exact';
import type { Rat } from './exact';
import { normalize, polyRat, polyTerms, rfSimplify } from './normal';
import { sub, type Statement, type Term } from './term';
import type { StepCheck } from './types';
import { unknownCheck } from './types';

/** Forme linéaire Σ coef·x + c (l'équation est « forme = 0 »). Coefficients nuls absents. */
export interface LinearForm {
  readonly coef: ReadonlyMap<string, Rat>;
  readonly c: Rat;
}

/** Au-delà, on ne tente pas l'élimination (réponse honnête : inconnu). */
export const MAX_SYSTEM_UNKNOWNS = 12;
export const MAX_SYSTEM_EQUATIONS = 40;

/* ------------------------------ lecture ------------------------------ */

/** Terme → forme linéaire (null si le terme n'est pas affine à coefficients rationnels). */
export function linearFormOfTerm(t: Term): LinearForm | null {
  const n = normalize(t);
  if (!n.ok) return null;
  const rf = rfSimplify(n.rf);
  const d = polyRat(rf.d);
  if (!d || Q.isZero(d)) return null;
  const coef = new Map<string, Rat>();
  let c = Q.ZERO;
  for (const { m, c: k } of polyTerms(rf.n)) {
    const v = Q.div(k, d)!;
    if (m.length === 0) c = Q.add(c, v);
    else if (m.length === 1 && m[0]!.e === 1 && m[0]!.a.kind === 'sym') {
      const s = m[0]!.a.symbol!;
      const prev = coef.get(s) ?? Q.ZERO;
      const next = Q.add(prev, v);
      if (Q.isZero(next)) coef.delete(s);
      else coef.set(s, next);
    } else return null;
  }
  return { coef, c };
}

/** Énoncé « l = r » → forme de l − r ; null pour autre chose qu'une égalité linéaire. */
export function linearForm(s: Statement): LinearForm | null {
  if (s.k !== 'rel' || s.rel !== '=') return null;
  return linearFormOfTerm(sub(s.l, s.r));
}

export function formUnknowns(forms: readonly LinearForm[]): string[] {
  const out = new Set<string>();
  for (const f of forms) for (const s of f.coef.keys()) out.add(s);
  return [...out].sort();
}

/** « 0 = 0 » (aucune information). */
export function isTrivialForm(f: LinearForm): boolean {
  return f.coef.size === 0 && Q.isZero(f.c);
}
/** « 0 = k », k ≠ 0 : une contradiction. */
export function isContradictionForm(f: LinearForm): boolean {
  return f.coef.size === 0 && !Q.isZero(f.c);
}

/** Valeur de la forme pour des valeurs exactes des inconnues (inconnue absente : 0). */
export function evalForm(f: LinearForm, values: ReadonlyMap<string, Rat>): Rat {
  let v = f.c;
  for (const [s, k] of f.coef) v = Q.add(v, Q.mul(k, values.get(s) ?? Q.ZERO));
  return v;
}

/* ------------------------------ élimination ------------------------------ */

/** Coordonnées d'une forme sur une base de clés (inconnues puis « » pour la constante). */
function vectorOf(f: LinearForm, keys: readonly string[]): Rat[] {
  return keys.map((k) => (k === '' ? f.c : (f.coef.get(k) ?? Q.ZERO)));
}

/**
 * Résout M·λ = b (M : m lignes × n colonnes) exactement. Renvoie une solution
 * (variables libres à 0) ou null si le système est incompatible.
 */
function solveColumns(M: Rat[][], b: Rat[], n: number): Rat[] | null {
  const m = M.length;
  const A = M.map((row, i) => [...row, b[i]!]);
  const pivots: number[] = [];
  let r = 0;
  for (let col = 0; col < n && r < m; col++) {
    let p = -1;
    for (let i = r; i < m; i++)
      if (!Q.isZero(A[i]![col]!)) {
        p = i;
        break;
      }
    if (p < 0) continue;
    [A[r], A[p]] = [A[p]!, A[r]!];
    const inv = Q.div(Q.ONE, A[r]![col]!)!;
    A[r] = A[r]!.map((x) => Q.mul(x, inv));
    for (let i = 0; i < m; i++) {
      if (i === r || Q.isZero(A[i]![col]!)) continue;
      const k = A[i]![col]!;
      A[i] = A[i]!.map((x, j) => Q.sub(x, Q.mul(k, A[r]![j]!)));
    }
    pivots.push(col);
    r++;
  }
  for (let i = r; i < m; i++) if (!Q.isZero(A[i]![n]!)) return null;
  const sol = Array.from({ length: n }, () => Q.ZERO);
  pivots.forEach((col, i) => (sol[col] = A[i]![n]!));
  return sol;
}

/**
 * La forme f est-elle une combinaison linéaire des formes `rows` ?
 * Renvoie les coefficients λ (f = Σ λᵢ·rowsᵢ) ou null.
 */
export function combinationOf(rows: readonly LinearForm[], f: LinearForm): Rat[] | null {
  if (rows.length === 0) return isTrivialForm(f) ? [] : null;
  if (rows.length > MAX_SYSTEM_EQUATIONS) return null;
  const keys = [...formUnknowns([...rows, f]), ''];
  if (keys.length - 1 > MAX_SYSTEM_UNKNOWNS) return null;
  const cols = rows.map((r) => vectorOf(r, keys));
  const M = keys.map((_, i) => cols.map((col) => col[i]!));
  return solveColumns(M, vectorOf(f, keys), rows.length);
}

export type SystemSolution =
  | { kind: 'unique'; values: ReadonlyMap<string, Rat> }
  | { kind: 'none' }
  | {
      kind: 'infinite';
      /** une solution particulière (inconnues libres à 0) */
      particular: ReadonlyMap<string, Rat>;
      /** directions : particulière + Σ tⱼ·directionⱼ décrit toutes les solutions */
      directions: ReadonlyMap<string, Rat>[];
      free: string[];
    }
  | { kind: 'too_big' };

/** Résolution exacte d'un système de formes linéaires (inconnues = celles des formes, ou `unknowns`). */
export function solveLinearSystem(
  forms: readonly LinearForm[],
  unknowns: readonly string[] = formUnknowns(forms),
): SystemSolution {
  const vars = [...unknowns];
  if (vars.length > MAX_SYSTEM_UNKNOWNS || forms.length > MAX_SYSTEM_EQUATIONS)
    return { kind: 'too_big' };
  const n = vars.length;
  const A = forms.map((f) => [...vars.map((v) => f.coef.get(v) ?? Q.ZERO), Q.neg(f.c)]);
  const pivots: number[] = [];
  let r = 0;
  for (let col = 0; col < n && r < A.length; col++) {
    let p = -1;
    for (let i = r; i < A.length; i++)
      if (!Q.isZero(A[i]![col]!)) {
        p = i;
        break;
      }
    if (p < 0) continue;
    [A[r], A[p]] = [A[p]!, A[r]!];
    const inv = Q.div(Q.ONE, A[r]![col]!)!;
    A[r] = A[r]!.map((x) => Q.mul(x, inv));
    for (let i = 0; i < A.length; i++) {
      if (i === r || Q.isZero(A[i]![col]!)) continue;
      const k = A[i]![col]!;
      A[i] = A[i]!.map((x, j) => Q.sub(x, Q.mul(k, A[r]![j]!)));
    }
    pivots.push(col);
    r++;
  }
  for (let i = r; i < A.length; i++) if (!Q.isZero(A[i]![n]!)) return { kind: 'none' };
  const pointWith = (free: ReadonlyMap<number, Rat>, homogeneous: boolean): Map<string, Rat> => {
    const out = new Map<string, Rat>();
    for (const [col, v] of free) out.set(vars[col]!, v);
    pivots.forEach((col, i) => {
      let v = homogeneous ? Q.ZERO : A[i]![n]!;
      for (const [fc, fv] of free) v = Q.sub(v, Q.mul(A[i]![fc]!, fv));
      out.set(vars[col]!, v);
    });
    for (let c = 0; c < n; c++) if (!out.has(vars[c]!)) out.set(vars[c]!, Q.ZERO);
    return out;
  };
  const freeCols = vars.map((_, c) => c).filter((c) => !pivots.includes(c));
  if (freeCols.length === 0) return { kind: 'unique', values: pointWith(new Map(), false) };
  return {
    kind: 'infinite',
    particular: pointWith(new Map(freeCols.map((c) => [c, Q.ZERO])), false),
    directions: freeCols.map((c) =>
      pointWith(new Map(freeCols.map((fc) => [fc, fc === c ? Q.ONE : Q.ZERO])), true),
    ),
    free: freeCols.map((c) => vars[c]!),
  };
}

/** Deux équations disent-elles la même chose (formes proportionnelles, non triviales) ? */
export function sameEquation(f: LinearForm, g: LinearForm): boolean {
  if (isTrivialForm(f) || isTrivialForm(g)) return isTrivialForm(f) && isTrivialForm(g);
  return combinationOf([f], g) !== null;
}

/* ------------------------------ verdict ------------------------------ */

function valuesText(values: ReadonlyMap<string, Rat>): string {
  return [...values]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([s, v]) => `${s} = ${Q.ratToText(v)}`)
    .join(', ');
}

/**
 * Une solution du système `facts` qui n'est PAS solution de `target`
 * (existe dès que le système est compatible et que target n'est pas une conséquence).
 */
export function counterexampleOf(
  facts: readonly LinearForm[],
  target: LinearForm,
): ReadonlyMap<string, Rat> | null {
  const vars = formUnknowns([...facts, target]);
  const sol = solveLinearSystem(facts, vars);
  if (sol.kind === 'unique') return Q.isZero(evalForm(target, sol.values)) ? null : sol.values;
  if (sol.kind !== 'infinite') return null;
  if (!Q.isZero(evalForm(target, sol.particular))) return sol.particular;
  for (const dir of sol.directions) {
    const pt = new Map<string, Rat>();
    for (const v of vars) pt.set(v, Q.add(sol.particular.get(v)!, dir.get(v)!));
    if (!Q.isZero(evalForm(target, pt))) return pt;
  }
  return null;
}

/**
 * La ligne `target` découle-t-elle du système `facts` ?
 *   - valide (déduction exacte) si sa forme est une combinaison des faits ;
 *   - invalide (contre-exemple exact) si le système est compatible et qu'une de
 *     ses solutions ne la vérifie pas ;
 *   - inconnu si quelque chose n'est pas linéaire ou trop gros ; d'un système
 *     impossible on n'accepte que les combinaisons (dont la contradiction
 *     « 0 = k »), jamais « tout et n'importe quoi ».
 */
export function checkSystemConsequence(facts: readonly Statement[], target: Statement): StepCheck {
  const forms: LinearForm[] = [];
  for (const s of facts) {
    const f = linearForm(s);
    if (!f) return unknownCheck('not_linear');
    forms.push(f);
  }
  const t = linearForm(target);
  if (!t) return unknownCheck('not_linear');
  return checkFormConsequence(forms, t);
}

/** Même verdict, sur des formes déjà lues. */
export function checkFormConsequence(facts: readonly LinearForm[], target: LinearForm): StepCheck {
  if (isTrivialForm(target))
    return {
      status: 'valid',
      relation: 'identity',
      proof: 'exact',
      explanation: { code: 'trivial' },
    };
  const sol = solveLinearSystem(facts, formUnknowns([...facts, target]));
  if (sol.kind === 'too_big') return unknownCheck('system_too_big');
  const lambdas = combinationOf(facts, target);
  if (lambdas)
    return {
      status: 'valid',
      relation: 'deduction',
      proof: 'exact',
      explanation: {
        code: 'linear_combination',
        params: { coefficients: lambdas.map(Q.ratToText).join(', ').slice(0, 400) },
      },
    };
  if (sol.kind === 'none') return unknownCheck('inconsistent_hypotheses');
  const w = counterexampleOf(facts, target);
  if (!w) return unknownCheck('no_witness');
  return {
    status: 'invalid',
    relation: 'none',
    proof: 'exact',
    explanation: { code: 'counterexample', params: { witness: valuesText(w).slice(0, 400) } },
  };
}
